import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Package, Plus, Search, ChevronDown, QrCode, Printer, Download, CheckSquare, Square, Camera, ScanLine } from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';
import { EquipmentDetailDialog, AssignDialog, ReturnDialog } from '../components/inventory/InventoryDialogs';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORY_LABELS = {
  investigation_tools: 'Investigation Tools',
  recorders_readout: 'Recorder Readout',
  underwater_salvage: 'Underwater & Salvage',
  lab_analysis: 'Lab & Analysis',
  safety_ppe: 'Safety & PPE',
  communication: 'Communication',
  documentation: 'Documentation',
  vehicles_vessels: 'Vehicles & Vessels',
  it_computing: 'IT & Computing',
  other: 'Other',
};

const STATUS_STYLES = {
  available: { bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  in_use: { bg: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  maintenance: { bg: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  calibration: { bg: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  retired: { bg: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' },
};

const CONDITION_STYLES = {
  new: 'text-emerald-600', good: 'text-blue-600', fair: 'text-amber-600',
  poor: 'text-orange-600', needs_repair: 'text-red-600', decommissioned: 'text-gray-400',
};

const EMPTY_FORM = {
  name: '', category: 'investigation_tools', sector: 'both', serial_number: '',
  manufacturer: '', model: '', condition: 'good', status: 'available',
  location: '', storage_location: '', purchase_date: '', purchase_cost: '',
  calibration_due: '', last_service_date: '', next_service_date: '',
  notes: '', specifications: '',
};

export default function Inventory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showAssign, setShowAssign] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [investigations, setInvestigations] = useState([]);
  const [assignForm, setAssignForm] = useState({ investigation_id: '', assigned_to: '' });
  const [returnForm, setReturnForm] = useState({ condition: 'good', notes: '' });
  const [showReturn, setShowReturn] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showFormScanner, setShowFormScanner] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const canManage = user?.role === 'admin' || user?.role === 'iic';

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterSector) params.append('sector', filterSector);
      if (filterCategory) params.append('category', filterCategory);
      if (filterStatus) params.append('status', filterStatus);
      if (search) params.append('q', search);
      const [itemsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/inventory?${params}`, { credentials: 'include' }),
        fetch(`${API}/api/inventory/stats/summary`, { credentials: 'include' }),
      ]);
      if (itemsRes.ok) setItems(await itemsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { /* error handled silently */ }
    setLoading(false);
  }, [filterSector, filterCategory, filterStatus, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadInvestigations = async () => {
    try {
      const res = await fetch(`${API}/api/investigations`, { credentials: 'include' });
      if (res.ok) setInvestigations(await res.json());
    } catch (e) { /* error handled silently */ }
  };

  const handleSave = async () => {
    const url = editingId ? `${API}/api/inventory/${editingId}` : `${API}/api/inventory`;
    const method = editingId ? 'PUT' : 'POST';
    const res = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (res.ok) { setShowAdd(false); setEditingId(null); setForm(EMPTY_FORM); loadData(); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this equipment?')) return;
    await fetch(`${API}/api/inventory/${id}`, { method: 'DELETE', credentials: 'include' });
    setShowDetail(null); loadData();
  };

  const handleAssign = async () => {
    const res = await fetch(`${API}/api/inventory/${showAssign}/assign`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(assignForm) });
    if (res.ok) { setShowAssign(null); setAssignForm({ investigation_id: '', assigned_to: '' }); setShowDetail(null); loadData(); }
  };

  const handleReturn = async () => {
    const res = await fetch(`${API}/api/inventory/${showReturn}/return`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(returnForm) });
    if (res.ok) { setShowReturn(null); setReturnForm({ condition: 'good', notes: '' }); setShowDetail(null); loadData(); }
  };

  const loadQrPreview = async (itemId) => {
    setLoadingQr(true);
    try {
      const res = await fetch(`${API}/api/inventory/${itemId}/qr-image`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        setQrPreview(URL.createObjectURL(blob));
      }
    } catch (e) { /* error handled silently */ }
    setLoadingQr(false);
  };

  const downloadQrLabel = async (itemId, assetTag) => {
    const res = await fetch(`${API}/api/inventory/${itemId}/qr-label`, { credentials: 'include' });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QR_Label_${assetTag}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const printQrLabel = async (itemId) => {
    const res = await fetch(`${API}/api/inventory/${itemId}/qr-label`, { credentials: 'include' });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url);
      if (w) { w.onload = () => { w.print(); }; }
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === items.length) setSelectedItems(new Set());
    else setSelectedItems(new Set(items.map(i => i.id)));
  };

  const downloadBatchLabels = async () => {
    const ids = Array.from(selectedItems);
    if (ids.length === 0) return;
    const res = await fetch(`${API}/api/inventory/batch-qr-labels`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_ids: ids }),
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RootTrace_Batch_QR_${ids.length}_Labels.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const printBatchLabels = async () => {
    const ids = Array.from(selectedItems);
    if (ids.length === 0) return;
    const res = await fetch(`${API}/api/inventory/batch-qr-labels`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item_ids: ids }),
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url);
      if (w) { w.onload = () => { w.print(); }; }
    }
  };

  // Parse scanned data and determine action
  const handleScanResult = (scannedText) => {
    setShowScanner(false);
    setScanResult(null);

    // Try to parse as RootTrace QR code
    try {
      const data = JSON.parse(scannedText);
      if (data.v === 'VIMANA' || data.v === 'RootTrace') {
        if (data.id) {
          // It's a RootTrace equipment QR — look up the item
          const found = items.find(i => i.id === data.id);
          if (found) {
            setShowDetail(found);
            return;
          }
          // Item not in current list, try fetching it
          fetch(`${API}/api/inventory/${data.id}`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(item => {
              if (item) {
                setShowDetail(item);
              } else {
                setScanResult({ type: 'not_found', text: scannedText, tag: data.tag });
              }
            });
          return;
        }
      }
      if ((data.system === 'VIMANA' || data.system === 'RootTrace') && data.id) {
        // PNG QR format
        const found = items.find(i => i.id === data.id);
        if (found) { setShowDetail(found); return; }
        fetch(`${API}/api/inventory/${data.id}`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null)
          .then(item => item ? setShowDetail(item) : setScanResult({ type: 'not_found', text: scannedText }));
        return;
      }
    } catch {
      // Not JSON — treat as barcode/serial number
    }

    // Search by the scanned text (serial number, asset tag, etc.)
    const searchText = scannedText.trim();
    const found = items.find(i =>
      i.serial_number === searchText ||
      i.asset_tag === searchText ||
      i.name === searchText
    );
    if (found) {
      setShowDetail(found);
      return;
    }

    // Not found — show result with option to search or create
    setScanResult({ type: 'unknown', text: searchText });
  };

  // Handle scan from Add Equipment form — fills serial number
  const handleFormScan = (scannedText) => {
    setShowFormScanner(false);
    let serialValue = scannedText.trim();

    // Try to extract serial from JSON QR
    try {
      const data = JSON.parse(scannedText);
      if (data.sn) serialValue = data.sn;
      else if (data.serial_number) serialValue = data.serial_number;
      else if (data.serial) serialValue = data.serial;
      // Also try to fill other fields if available
      setForm(prev => ({
        ...prev,
        serial_number: serialValue,
        ...(data.manufacturer ? { manufacturer: data.manufacturer } : {}),
        ...(data.model ? { model: data.model } : {}),
        ...(data.name ? { name: prev.name || data.name } : {}),
      }));
      return;
    } catch {
      // Plain barcode — use as serial number
    }

    setForm(prev => ({ ...prev, serial_number: serialValue }));
  };

  const handleScanResultAction = (action) => {
    if (action === 'search') {
      setSearch(scanResult.text);
      setScanResult(null);
    } else if (action === 'create' && canManage) {
      setForm({ ...EMPTY_FORM, serial_number: scanResult.text });
      setEditingId(null);
      setShowAdd(true);
      setScanResult(null);
    } else {
      setScanResult(null);
    }
  };

  const openEdit = (item) => {
    setForm({
      name: item.name || '', category: item.category || 'other', sector: item.sector || 'both',
      serial_number: item.serial_number || '', manufacturer: item.manufacturer || '',
      model: item.model || '', condition: item.condition || 'good', status: item.status || 'available',
      location: item.location || '', storage_location: item.storage_location || '',
      purchase_date: item.purchase_date || '', purchase_cost: item.purchase_cost || '',
      calibration_due: item.calibration_due || '', last_service_date: item.last_service_date || '',
      next_service_date: item.next_service_date || '', notes: item.notes || '',
      specifications: item.specifications || '',
    });
    setEditingId(item.id);
    setShowAdd(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading inventory...</div>;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Equipment Inventory</h1>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">Manage investigation equipment for Aviation & Maritime sectors</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Scan button — available to ALL roles */}
          <Button
            data-testid="scan-code-btn"
            variant="outline"
            size="sm"
            onClick={() => setShowScanner(true)}
            className="border-gray-500 text-gray-300 hover:bg-gray-700">
            <Camera className="w-4 h-4 mr-1.5" /> Scan Code
          </Button>
          {canManage && (
            <>
              <Button
                data-testid="batch-qr-toggle"
                variant={batchMode ? "default" : "outline"}
                size="sm"
                onClick={() => { setBatchMode(!batchMode); setSelectedItems(new Set()); }}
                className={batchMode ? "bg-purple-600 hover:bg-purple-700 text-white" : "border-gray-500 text-gray-300 hover:bg-gray-700"}>
                <QrCode className="w-4 h-4 mr-1.5" /> {batchMode ? 'Cancel Batch' : 'Batch Labels'}
              </Button>
              <Button data-testid="add-equipment-btn" onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowAdd(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white" size="sm">
                <Plus className="w-4 h-4 mr-1.5" /> Add Equipment
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Batch actions bar */}
      {batchMode && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-purple-600/10 border border-purple-500/30 rounded-xl px-4 py-3" data-testid="batch-actions-bar">
          <div className="flex items-center gap-3">
            <button onClick={toggleSelectAll} className="text-purple-300 hover:text-white transition-colors" data-testid="batch-select-all">
              {selectedItems.size === items.length && items.length > 0
                ? <CheckSquare className="w-5 h-5" />
                : <Square className="w-5 h-5" />}
            </button>
            <span className="text-sm text-purple-200">
              {selectedItems.size} of {items.length} selected
            </span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={selectedItems.size === 0}
              data-testid="batch-download-btn"
              onClick={downloadBatchLabels}
              className="border-purple-400 text-purple-200 hover:bg-purple-700/30 disabled:opacity-40">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
            </Button>
            <Button size="sm" disabled={selectedItems.size === 0}
              data-testid="batch-print-btn"
              onClick={printBatchLabels}
              className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print Labels
            </Button>
          </div>
        </div>
      )}

      {/* Stats Bar - matches Correspondence style */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-0 rounded-xl overflow-hidden" data-testid="inventory-stats">
          {[
            { label: 'Total', value: stats.total, active: true, color: 'bg-blue-600' },
            { label: 'Available', value: stats.available, active: false, color: 'bg-emerald-600' },
            { label: 'In Use', value: stats.in_use, active: false, color: 'bg-blue-500' },
            { label: 'Maintenance', value: stats.maintenance, active: false, color: 'bg-amber-500' },
            { label: 'Retired', value: stats.retired, active: false, color: 'bg-gray-500' },
          ].map((s, i) => (
            <div key={s.label}
              className={`p-4 text-center ${i === 0 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
              <div className={`text-2xl font-bold ${i === 0 ? 'text-white' : s.value > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{s.value}</div>
              <div className={`text-xs mt-1 ${i === 0 ? 'text-blue-100' : 'text-gray-500'}`}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters - white/light theme */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center" data-testid="inventory-filters">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="inventory-search" placeholder="Search equipment..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400" />
        </div>
        <div className="relative">
          <select data-testid="filter-sector" value={filterSector} onChange={e => setFilterSector(e.target.value)}
            className="appearance-none bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 pr-8 text-sm cursor-pointer">
            <option value="">All Sectors</option>
            <option value="aviation">Aviation</option>
            <option value="maritime">Maritime</option>
            <option value="both">General</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select data-testid="filter-category" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="appearance-none bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 pr-8 text-sm cursor-pointer">
            <option value="">All Categories</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select data-testid="filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="appearance-none bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 pr-8 text-sm cursor-pointer">
            <option value="">All Status</option>
            <option value="available">Available</option>
            <option value="in_use">In Use</option>
            <option value="maintenance">Maintenance</option>
            <option value="calibration">Calibration</option>
            <option value="retired">Retired</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Equipment List - white cards, dark text */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="inventory-list">
        {items.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No equipment found. {canManage ? 'Click "Add Equipment" to get started.' : ''}</p>
          </div>
        ) : items.map((item, idx) => (
          <div key={item.id}
            data-testid={`equipment-item-${item.asset_tag}`}
            onClick={() => batchMode ? toggleItemSelection(item.id) : setShowDetail(item)}
            className={`flex items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors ${idx > 0 ? 'border-t border-gray-100' : ''} ${batchMode && selectedItems.has(item.id) ? 'bg-purple-50' : ''}`}>
            <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
              {batchMode && (
                <div className="shrink-0">
                  {selectedItems.has(item.id)
                    ? <CheckSquare className="w-5 h-5 text-purple-600" />
                    : <Square className="w-5 h-5 text-gray-400" />}
                </div>
              )}
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 text-sm sm:text-base truncate">{item.name}</span>
                  <span className="text-xs text-gray-400 font-mono hidden sm:inline">{item.asset_tag}</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-xs text-gray-500 mt-0.5 sm:mt-1 flex-wrap">
                  <span>{CATEGORY_LABELS[item.category] || item.category}</span>
                  {item.serial_number && <span className="hidden sm:inline">S/N: {item.serial_number}</span>}
                  {item.manufacturer && <span className="hidden md:inline">{item.manufacturer} {item.model}</span>}
                  {item.location && <span className="hidden lg:inline">{item.location}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 ml-2 sm:ml-4">
              <span className={`inline-flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-medium px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full ${(STATUS_STYLES[item.status] || STATUS_STYLES.available).bg}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${(STATUS_STYLES[item.status] || STATUS_STYLES.available).dot}`}></span>
                <span className="hidden sm:inline">{(item.status || '').replace('_', ' ')}</span>
              </span>
              <span className="text-[10px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-gray-100 text-gray-600">
                {item.sector === 'both' ? 'GEN' : item.sector === 'aviation' ? 'AVN' : 'MAR'}
              </span>
              {item.assigned_investigation_number && (
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-600 hidden sm:inline">
                  {item.assigned_investigation_number}
                </span>
              )}
              <ChevronDown className="w-4 h-4 text-gray-300 -rotate-90 hidden sm:block" />
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Dialog - white theme */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[85vh] overflow-y-auto w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900">{editingId ? 'Edit Equipment' : 'Add Equipment'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            {/* Scan to fill fields */}
            <div className="sm:col-span-2">
              <Button variant="outline" size="sm" type="button"
                data-testid="form-scan-btn"
                onClick={() => setShowFormScanner(true)}
                className="w-full border-dashed border-2 border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400 py-3">
                <Camera className="w-4 h-4 mr-2" />
                Scan Barcode / QR Code to auto-fill fields
              </Button>
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-700">Name *</label>
              <Input data-testid="eq-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" placeholder="Equipment name" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Category</label>
              <select data-testid="eq-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full mt-1 bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 text-sm">
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Sector</label>
              <select data-testid="eq-sector" value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })}
                className="w-full mt-1 bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 text-sm">
                <option value="both">Both (General)</option>
                <option value="aviation">Aviation</option>
                <option value="maritime">Maritime</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Serial Number</label>
              <div className="relative mt-1">
                <Input value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })}
                  className="bg-white border-gray-300 text-gray-900 pr-10" placeholder="Scan or type..." />
                <button type="button" onClick={() => setShowFormScanner(true)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                  data-testid="serial-scan-btn" title="Scan barcode">
                  <Camera className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Manufacturer</label>
              <Input value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Model</label>
              <Input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Condition</label>
              <select value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })}
                className="w-full mt-1 bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 text-sm">
                <option value="new">New</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="needs_repair">Needs Repair</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Location</label>
              <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" placeholder="Current location" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Storage Location</label>
              <Input value={form.storage_location} onChange={e => setForm({ ...form, storage_location: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" placeholder="Default storage" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Purchase Date</label>
              <Input type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Purchase Cost</label>
              <Input value={form.purchase_cost} onChange={e => setForm({ ...form, purchase_cost: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" placeholder="e.g. $5,000" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Calibration Due</label>
              <Input type="date" value={form.calibration_due} onChange={e => setForm({ ...form, calibration_due: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Last Service Date</label>
              <Input type="date" value={form.last_service_date} onChange={e => setForm({ ...form, last_service_date: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Next Service Date</label>
              <Input type="date" value={form.next_service_date} onChange={e => setForm({ ...form, next_service_date: e.target.value })}
                className="bg-white border-gray-300 text-gray-900 mt-1" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-700">Specifications</label>
              <textarea value={form.specifications} onChange={e => setForm({ ...form, specifications: e.target.value })}
                className="w-full mt-1 bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 text-sm min-h-[60px]"
                placeholder="Technical specifications..." />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="w-full mt-1 bg-white border border-gray-300 text-gray-700 rounded-md px-3 py-2 text-sm min-h-[60px]"
                placeholder="Additional notes..." />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAdd(false)} className="border-gray-300 text-gray-700">Cancel</Button>
            <Button data-testid="eq-save-btn" onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white">
              {editingId ? 'Update' : 'Add Equipment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <EquipmentDetailDialog item={showDetail} open={!!showDetail} onClose={() => { setShowDetail(null); setQrPreview(null); }}
        canManage={canManage} qrPreview={qrPreview} loadingQr={loadingQr} onLoadQr={loadQrPreview}
        onDownloadQr={downloadQrLabel} onPrintQr={printQrLabel}
        onEdit={(item) => { openEdit(item); setShowDetail(null); }}
        onAssign={(itemId) => { loadInvestigations(); setShowAssign(itemId); }}
        onReturn={(itemId) => setShowReturn(itemId)}
        onDelete={handleDelete} />

      {/* Assign Dialog */}
      <AssignDialog open={!!showAssign} onClose={() => setShowAssign(null)}
        investigations={investigations} form={assignForm} setForm={setAssignForm} onAssign={handleAssign} />

      {/* Return Dialog */}
      <ReturnDialog open={!!showReturn} onClose={() => setShowReturn(null)}
        form={returnForm} setForm={setReturnForm} onReturn={handleReturn} />

      {/* Page Scanner — quick lookup for all users */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleScanResult}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Form Scanner — serial number input during Add Equipment */}
      {showFormScanner && (
        <BarcodeScanner
          onScan={handleFormScan}
          onClose={() => setShowFormScanner(false)}
        />
      )}

      {/* Scan Result Dialog */}
      <Dialog open={!!scanResult} onOpenChange={() => setScanResult(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <ScanLine className="w-5 h-5 text-blue-500" />
              Scan Result
            </DialogTitle>
          </DialogHeader>
          {scanResult && (
            <div className="space-y-4 mt-2">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Scanned value:</div>
                <div className="text-sm font-mono text-gray-900 break-all" data-testid="scan-result-value">{scanResult.text}</div>
              </div>

              {scanResult.type === 'not_found' && (
                <p className="text-sm text-amber-600">
                  {scanResult.tag ? `Equipment "${scanResult.tag}" was not found in the current inventory.` : 'This RootTrace equipment was not found in the database.'}
                </p>
              )}

              {scanResult.type === 'unknown' && (
                <p className="text-sm text-gray-600">
                  No matching equipment found. You can search the inventory or create a new entry.
                </p>
              )}

              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="border-gray-300 text-gray-700 flex-1"
                  data-testid="scan-result-search"
                  onClick={() => handleScanResultAction('search')}>
                  <Search className="w-3.5 h-3.5 mr-1.5" /> Search Inventory
                </Button>
                {canManage && (
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                    data-testid="scan-result-create"
                    onClick={() => handleScanResultAction('create')}>
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add as New
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
